/**
 * Rendering boundary for persisted GPC controller data. Controller views are
 * server data and older sessions may contain structured candidates where a
 * modern field expects prose. Never pass those values directly into JSX or
 * form attributes.
 */
export function displayLabel(key: string): string {
  return key.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function safeDisplayText(value: unknown, fallback = ''): string {
  const seen = new WeakSet<object>();

  const visit = (current: unknown, depth: number): string => {
    if (typeof current === 'string' || typeof current === 'number' || typeof current === 'boolean') {
      return String(current);
    }
    if (current === null || current === undefined) return '';
    if (depth > 5) return '…';
    if (Array.isArray(current)) return current.map((item) => visit(item, depth + 1)).filter(Boolean).join(', ');
    if (typeof current === 'object') {
      if (seen.has(current)) return '[circular value]';
      seen.add(current);
      return Object.entries(current as Record<string, unknown>)
        .map(([key, nested]) => `${displayLabel(key)}: ${visit(nested, depth + 1) || '—'}`)
        .join('\n');
    }
    return String(current);
  };

  return visit(value, 0) || fallback;
}

export type SafeFormValue = string | number | boolean;

export function safeFormValue(value: unknown): SafeFormValue | undefined {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value && typeof value === 'object') return safeDisplayText(value);
  return undefined;
}
